import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { API_BASE_URL } from '../../core/config/api.config';
import { RecipeCategory, RecipeCategoryPayload } from '../../core/models/admin-recipe.models';
import { AdminCategoriesService } from '../../core/services/admin-categories.service';
import { StorageService } from '../../core/services/storage.service';

@Component({
  selector: 'app-categories-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './categories-page.component.html',
  styleUrl: './categories-page.component.scss',
})
export class CategoriesPageComponent implements OnInit {
  private readonly categoriesService = inject(AdminCategoriesService);
  private readonly storageService = inject(StorageService);
  private readonly fb = inject(FormBuilder);
  private readonly apiHost = API_BASE_URL.replace(/\/api\/?$/, '');

  categories: RecipeCategory[] = [];
  visibleCategories: RecipeCategory[] = [];

  loading = false;
  saving = false;
  uploading = false;
  deletingCategoryId: number | null = null;
  editingCategoryId: number | null = null;
  showEditor = false;

  searchQuery = '';
  currentPage = 0;
  pageSize = 10;
  totalItems = 0;
  totalPages = 1;

  errorMessage = '';
  successMessage = '';

  readonly form = this.fb.group({
    name: ['', [Validators.required, Validators.minLength(4)]],
    description: ['', [Validators.required, Validators.minLength(10)]],
    imageUrl: [''],
    parentCategoryId: [null as number | null],
  });

  ngOnInit(): void {
    this.loadCategories();
  }

  loadCategories(): void {
    this.loading = true;
    this.errorMessage = '';

    this.categoriesService.list(0, 500).subscribe({
      next: (response) => {
        this.categories = response.items ?? [];
        this.applyFilters(true);
      },
      error: (error) => {
        this.errorMessage = error?.error?.message ?? 'Impossible de charger les catégories.';
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  applySearch(query: string): void {
    this.searchQuery = query;
    this.applyFilters(true);
  }

  previousPage(): void {
    if (this.currentPage <= 0) {
      return;
    }
    this.currentPage -= 1;
    this.applyFilters(false);
  }

  nextPage(): void {
    if (this.currentPage + 1 >= this.totalPages) {
      return;
    }
    this.currentPage += 1;
    this.applyFilters(false);
  }

  startCreate(): void {
    this.showEditor = true;
    this.editingCategoryId = null;
    this.errorMessage = '';
    this.successMessage = '';
    this.form.reset({
      name: '',
      description: '',
      imageUrl: '',
      parentCategoryId: null,
    });
  }

  closeEditor(): void {
    this.showEditor = false;
    this.editingCategoryId = null;
  }

  startEdit(category: RecipeCategory): void {
    this.showEditor = true;
    this.editingCategoryId = category.id;
    this.errorMessage = '';
    this.successMessage = '';
    this.form.patchValue({
      name: category.name ?? '',
      description: category.description ?? '',
      imageUrl: category.imageUrl ?? '',
      parentCategoryId: category.parentId ?? null,
    });
  }

  async save(): Promise<void> {
    if (this.form.invalid || this.saving || this.uploading) {
      this.form.markAllAsTouched();
      return;
    }

    const name = this.form.get('name')?.value?.trim();
    const description = this.form.get('description')?.value?.trim();
    if (!name || !description) {
      this.errorMessage = 'Nom et description sont obligatoires.';
      return;
    }

    const payload: RecipeCategoryPayload = {
      name,
      description,
      imageUrl: this.form.get('imageUrl')?.value?.trim() || null,
    };

    const parentCategoryId = this.normalizeOptionalNumber(this.form.get('parentCategoryId')?.value);
    if (this.editingCategoryId != null && parentCategoryId === this.editingCategoryId) {
      this.errorMessage = 'Une catégorie ne peut pas être sa propre catégorie parente.';
      return;
    }

    this.saving = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      if (this.editingCategoryId == null) {
        await firstValueFrom(this.categoriesService.create(payload, parentCategoryId));
        this.successMessage = 'Catégorie créée avec succès.';
      } else {
        await firstValueFrom(this.categoriesService.update(this.editingCategoryId, payload));
        this.successMessage = 'Catégorie modifiée avec succès.';
      }
      this.startCreate();
      this.loadCategories();
    } catch (error: unknown) {
      this.errorMessage = this.extractErrorMessage(error, 'Enregistrement impossible.');
    } finally {
      this.saving = false;
    }
  }

  async delete(category: RecipeCategory): Promise<void> {
    if (!category.id || this.deletingCategoryId != null) {
      return;
    }

    const confirmed = window.confirm(`Supprimer la catégorie "${category.name ?? '#' + category.id}" ?`);
    if (!confirmed) {
      return;
    }

    this.deletingCategoryId = category.id;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      await firstValueFrom(this.categoriesService.delete(category.id));
      this.successMessage = 'Catégorie supprimée avec succès.';
      if (this.editingCategoryId === category.id) {
        this.startCreate();
      }
      this.loadCategories();
    } catch (error: unknown) {
      this.errorMessage = this.extractErrorMessage(error, 'Suppression impossible.');
    } finally {
      this.deletingCategoryId = null;
    }
  }

  async uploadImage(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.item(0);
    if (!file || this.uploading) {
      return;
    }

    this.uploading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const fileName = await firstValueFrom(this.storageService.uploadFile(file));
      this.form.patchValue({ imageUrl: fileName });
      this.successMessage = 'Image uploadée.';
    } catch (error: unknown) {
      this.errorMessage = this.extractErrorMessage(error, 'Upload impossible.');
    } finally {
      this.uploading = false;
      if (input) {
        input.value = '';
      }
    }
  }

  isDeleting(categoryId: number): boolean {
    return this.deletingCategoryId === categoryId;
  }

  selectableParentCategories(): RecipeCategory[] {
    return this.categories.filter((category) => category.id !== this.editingCategoryId);
  }

  parentLabel(parentId: number | null | undefined): string {
    if (parentId == null) {
      return 'Aucune';
    }
    return this.categories.find((item) => item.id === parentId)?.name ?? `#${parentId}`;
  }

  categoryImageUrl(category: RecipeCategory): string | null {
    const imageValue = this.normalizeOptionalString(category.imageUrl);
    if (!imageValue) {
      return null;
    }
    if (imageValue.startsWith('http://') || imageValue.startsWith('https://')) {
      return imageValue;
    }
    if (imageValue.startsWith('/api/')) {
      return `${this.apiHost}${imageValue}`;
    }
    if (imageValue.startsWith('/storage/files/')) {
      return `${this.apiHost}/api${imageValue}`;
    }
    if (imageValue.startsWith('storage/files/')) {
      return `${this.apiHost}/api/${imageValue}`;
    }

    const normalized = imageValue.replace(/^\/+/, '');
    return `${API_BASE_URL}/storage/files/${normalized}`;
  }

  imagePreviewUrl(): string | null {
    const imageValue = this.normalizeOptionalString(this.form.get('imageUrl')?.value);
    if (!imageValue) {
      return null;
    }

    return this.categoryImageUrl({
      id: -1,
      imageUrl: imageValue,
    });
  }

  private applyFilters(resetPage: boolean): void {
    if (resetPage) {
      this.currentPage = 0;
    }

    const query = this.searchQuery.trim().toLowerCase();
    const filtered = this.categories.filter((category) => {
      if (!query) {
        return true;
      }
      const haystack = `${category.name ?? ''} ${category.description ?? ''}`.toLowerCase();
      return haystack.includes(query);
    });

    this.totalItems = filtered.length;
    this.totalPages = Math.max(1, Math.ceil(this.totalItems / this.pageSize));
    if (this.currentPage >= this.totalPages) {
      this.currentPage = this.totalPages - 1;
    }

    const from = this.currentPage * this.pageSize;
    this.visibleCategories = filtered.slice(from, from + this.pageSize);
  }

  private normalizeOptionalString(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  private normalizeOptionalNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.length === 0) {
        return null;
      }
      const parsed = Number(trimmed);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }

    return null;
  }

  private extractErrorMessage(error: unknown, fallback: string): string {
    if (typeof error === 'string' && error.trim().length > 0) {
      return error;
    }

    if (error && typeof error === 'object') {
      const record = error as { error?: { message?: unknown }; message?: unknown };
      if (typeof record.error?.message === 'string' && record.error.message.trim().length > 0) {
        return record.error.message;
      }
      if (typeof record.message === 'string' && record.message.trim().length > 0) {
        return record.message;
      }
    }

    return fallback;
  }
}
